/**
 * ContractorNotificationsPanel
 * ----------------------------
 * Shows recent admin-triggered alerts (RFQ invites, awarded bids, need-info
 * questions, rejections) on the contractor dashboard.
 *
 * Behaviour:
 *  - Polls /api/contractor/notifications on mount + every 60s
 *  - Each notification auto-clears (marks read) on click via POST .../read
 *  - For RFQ rows, exposes a "Submit Quote" CTA that opens an in-page modal
 *    posting through the public passwordless endpoint (works regardless of
 *    portal account status — the contractor's profile auto-fills name/email)
 *  - When all are read, the panel renders nothing
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bell, ExternalLink, Send, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const KIND_META = {
  rfq:        { color: "border-amber-300 bg-amber-50",  pill: "bg-amber-500" },
  awarded:    { color: "border-emerald-300 bg-emerald-50", pill: "bg-emerald-500" },
  need_info:  { color: "border-blue-300 bg-blue-50",    pill: "bg-blue-500" },
  rejected:   { color: "border-rose-300 bg-rose-50",    pill: "bg-rose-500" },
};

export default function ContractorNotificationsPanel({ token, contractor }) {
  const [items, setItems] = useState([]);
  const [quoting, setQuoting] = useState(null); // request_id currently being quoted
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/contractor/notifications`, { headers });
      setItems(r.data.notifications || []);
    } catch (_) {
      /* silent — keep last state */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const markRead = async (id) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n)));
    try {
      await axios.post(`${API}/contractor/notifications/${id}/read`, {}, { headers });
    } catch (_) {
      // optimistic UI; ignore failure
    }
  };

  const unread = items.filter((n) => !n.read_at);
  if (unread.length === 0 && !quoting) return null;

  return (
    <section
      className="mb-4 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden"
      data-testid="contractor-notifications-panel"
    >
      <header className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4 text-amber-500" />
          <h2 className="text-sm font-semibold text-slate-800">Notifications</h2>
          {unread.length > 0 && (
            <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-500 text-white px-1.5 py-0.5 rounded">
              {unread.length} new
            </span>
          )}
        </div>
      </header>
      <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
        {unread.map((n) => {
          const meta = KIND_META[n.kind] || { color: "border-slate-300 bg-slate-50", pill: "bg-slate-500" };
          return (
            <div
              key={n.id}
              className={`flex items-start gap-3 px-4 py-3 border-l-4 ${meta.color} hover:bg-slate-50`}
              data-testid={`contractor-notif-${n.id}`}
            >
              <span className={`w-2 h-2 mt-1.5 rounded-full ${meta.pill} flex-shrink-0`} />
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => markRead(n.id)}>
                <p className="text-sm font-semibold text-slate-900 truncate">{n.title}</p>
                <p className="text-xs text-slate-600">{n.body}</p>
                <p className="text-[10px] text-slate-400 mt-1">
                  {new Date(n.created_at).toLocaleString()}
                </p>
              </div>
              <div className="flex-shrink-0 flex flex-col items-end gap-1.5">
                {n.kind === "rfq" && n.request_id && (
                  <Button
                    size="sm"
                    onClick={() => {
                      setQuoting(n.request_id);
                      markRead(n.id);
                    }}
                    className="h-7 px-2.5 text-xs bg-amber-600 hover:bg-amber-700 gap-1"
                    data-testid={`contractor-notif-submit-${n.id}`}
                  >
                    <Send className="w-3 h-3" /> Submit Quote
                  </Button>
                )}
                {n.quote_url && (
                  <a
                    href={n.quote_url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-[11px] text-blue-600 hover:text-blue-800 flex items-center gap-0.5"
                    data-testid={`contractor-notif-link-${n.id}`}
                  >
                    Open <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {quoting && (
        <InlineQuoteSheet
          requestId={quoting}
          contractor={contractor}
          onClose={() => setQuoting(null)}
          onSubmitted={() => {
            setQuoting(null);
            load();
            toast.success("Quote submitted — Sterile Mate will be in touch shortly.");
          }}
        />
      )}
    </section>
  );
}

// ============================================================================
// Inline quote sheet — in-page Submit Quote affordance for logged-in contractors
// ============================================================================
function InlineQuoteSheet({ requestId, contractor, onClose, onSubmitted }) {
  const [loading, setLoading] = useState(true);
  const [req, setReq] = useState(null);
  const [labor, setLabor] = useState("");
  const [parts, setParts] = useState("");
  const [total, setTotal] = useState("");
  const [leadTime, setLeadTime] = useState("");
  const [earliest, setEarliest] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/maintenance-sourcing/${requestId}/public`);
        setReq(r.data);
      } catch (_) {
        toast.error("Could not load this RFQ");
        onClose();
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  const onMoney = (which, v) => {
    if (which === "labor") setLabor(v);
    else setParts(v);
    const l = which === "labor" ? Number(v || 0) : Number(labor || 0);
    const p = which === "parts" ? Number(v || 0) : Number(parts || 0);
    if (!Number.isNaN(l + p) && (l || p)) setTotal(String(l + p));
  };

  const submit = async () => {
    if (!total && !labor && !parts) {
      toast.error("Provide at least a total estimate or labor/parts breakdown");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/maintenance-sourcing/${requestId}/public-quote`, {
        contractor_name: contractor?.company_name || contractor?.name || "Contractor",
        contractor_email: contractor?.email,
        contractor_contact: contractor?.contact_name || undefined,
        contractor_phone: contractor?.phone || undefined,
        labor_total: labor ? Number(labor) : undefined,
        parts_total: parts ? Number(parts) : undefined,
        total_estimate: total ? Number(total) : undefined,
        lead_time: leadTime || undefined,
        earliest_start: earliest || undefined,
        notes: notes || undefined,
      });
      onSubmitted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="contractor-inline-quote-sheet">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 h-14 border-b border-slate-200 bg-gradient-to-r from-amber-50 to-white">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Send className="w-4 h-4 text-amber-600" /> Submit Quote — {requestId}
          </h3>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded" data-testid="contractor-quote-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-10">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {/* RFQ scope summary */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                Scope · Priority {req?.priority || "normal"}
              </div>
              {(req?.items?.length ? req.items : []).map((it, idx) => (
                <div key={idx} className="text-xs text-slate-700 py-1 border-t border-slate-100 first:border-t-0">
                  <strong>{(it.request_type || "issue").replace(/_/g, " ")}</strong>
                  {it.location_details && <span className="text-slate-500"> · {it.location_details}</span>}
                  <p className="text-slate-600">{it.description}</p>
                </div>
              )) || (
                <p className="text-sm text-slate-700">{req?.description || "(see RFQ email)"}</p>
              )}
            </div>

            {/* Quote inputs */}
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Labor ($)</Label>
                <Input type="number" min="0" step="0.01" value={labor} onChange={(e) => onMoney("labor", e.target.value)} data-testid="contractor-quote-labor" />
              </div>
              <div>
                <Label className="text-xs">Parts ($)</Label>
                <Input type="number" min="0" step="0.01" value={parts} onChange={(e) => onMoney("parts", e.target.value)} data-testid="contractor-quote-parts" />
              </div>
              <div>
                <Label className="text-xs">Total ($) *</Label>
                <Input type="number" min="0" step="0.01" value={total} onChange={(e) => setTotal(e.target.value)} required data-testid="contractor-quote-total" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Lead time</Label>
                <Input value={leadTime} onChange={(e) => setLeadTime(e.target.value)} placeholder="3-5 business days" />
              </div>
              <div>
                <Label className="text-xs">Earliest start</Label>
                <Input type="date" value={earliest} onChange={(e) => setEarliest(e.target.value)} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Scope assumptions, exclusions, COI status…" />
            </div>
            <p className="text-[11px] text-slate-500">
              Submitting as <strong>{contractor?.company_name || contractor?.name || "your account"}</strong> · {contractor?.email}
            </p>
          </div>
        )}

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting || loading}
            className="bg-amber-600 hover:bg-amber-700 gap-1.5"
            data-testid="contractor-quote-submit-btn"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Submit
          </Button>
        </div>
      </div>
    </div>
  );
}
